import { InputWrapper } from "@amzn/stencil-react-components/input";
import { Select } from "@amzn/stencil-react-components/select";
import { Text } from "@amzn/stencil-react-components/text";
import { Input } from "@amzn/uxm-stencil-react-components/lib/src/stencil/input";
import { TextArea } from "@amzn/uxm-stencil-react-components/lib/src/stencil/text-area";
import { getString } from "common/uistringlabels/uiStringUtils";
import React, { FC, useState } from "react";

export type FormFieldType = "text" | "textarea" | "select" | "multiSelect";

export interface FormFieldConfig {
  id: string;
  name: string;
  type: FormFieldType;
  labelKey: string;
  placeholderKey?: string;
  required?: boolean;
  footerKey?: string;
  options?: string[] | readonly string[] | { label: string; value: string }[];
  rows?: number;
  maxLength?: number;
}

interface FormFieldProps {
  config: FormFieldConfig;
  value: any;
  onChange: (value: any) => void;
  touchpoint?: string;
}

export const FormField: FC<FormFieldProps> = ({ config, value, onChange, touchpoint }) => {
  const [touched, setTouched] = useState(false);
  const label = getString(config.labelKey);
  const placeholder = config.placeholderKey ? getString(config.placeholderKey) : undefined;
  const footer = config.footerKey ? getString(config.footerKey) : undefined;

  const strValue = typeof value === "string" ? value : "";
  const isRequired = config.required;
  const isEmpty = isRequired && touched && (Array.isArray(value) ? value.length === 0 : strValue.trim() === "");
  const isNearLimit = config.maxLength && strValue.length > config.maxLength * 0.9;
  const isAtLimit = config.maxLength && strValue.length >= config.maxLength;

  const charCounter =
    config.maxLength && (config.type === "text" || config.type === "textarea") ? (
      <Text
        fontSize="T100"
        color={
          isAtLimit
            ? "var(--st-dt-color-text-error)"
            : isNearLimit
            ? "var(--st-dt-color-text-warning)"
            : "var(--st-dt-color-text-secondary)"
        }
        style={{ textAlign: "right", marginTop: 4 }}
      >
        {strValue.length}/{config.maxLength}
      </Text>
    ) : null;

  return (
    <InputWrapper
      id={config.id}
      labelText={label}
      required={config.required}
      footer={footer}
      error={isEmpty}
      {...(isEmpty && { errorMessage: getString("useCases.contributeForm.fieldRequired") })}
    >
      {(inputProps) => {
        switch (config.type) {
          case "text":
            return (
              <>
                <Input
                  {...inputProps}
                  touchpoint={touchpoint || config.id}
                  value={value || ""}
                  onChange={(e: any) => onChange(e.target.value)}
                  onBlur={() => setTouched(true)}
                  placeholder={placeholder}
                  maxLength={config.maxLength}
                />
                {charCounter}
              </>
            );
          case "textarea":
            return (
              <>
                <TextArea
                  {...inputProps}
                  touchpoint={touchpoint || config.id}
                  value={value || ""}
                  onChange={(e: any) => onChange(e.target.value)}
                  onBlur={() => setTouched(true)}
                  placeholder={placeholder}
                  resize="vertical"
                  rows={config.rows}
                  maxLength={config.maxLength}
                />
                {charCounter}
              </>
            );
          case "select": {
            const opts = (config.options as any[]) || [];
            const isObjectOptions = opts.length > 0 && typeof opts[0] === "object";
            const selectedOption = isObjectOptions ? opts.find((o) => o.value === value) : value;
            return (
              <Select
                {...inputProps}
                options={opts}
                value={selectedOption || undefined}
                isSearchable={true}
                onChange={(v: any) => {
                  setTouched(true);
                  onChange(isObjectOptions ? v?.value || "" : v || "");
                }}
                placeholder={placeholder}
                {...(isObjectOptions && { valueAccessor: "value", renderOption: (o: any) => o.label })}
              />
            );
          }
          case "multiSelect": {
            const opts = (config.options as any[]) || [];
            const isObjectOptions = opts.length > 0 && typeof opts[0] === "object";
            const selectedOptions = isObjectOptions ? opts.filter((o) => (value || []).includes(o.value)) : value || [];
            return (
              <div className="form-field-multiselect--drop-up">
                <Select
                  {...inputProps}
                  options={opts}
                  value={selectedOptions}
                  isMulti={true}
                  isSearchable={true}
                  onChange={(v: any) => {
                    setTouched(true);
                    if (!v) return onChange([]);
                    const arr = Array.isArray(v) ? v : [v];
                    onChange(isObjectOptions ? arr.map((o: any) => o.value) : arr);
                  }}
                  placeholder={placeholder}
                  listMaxHeight="240px"
                  {...(isObjectOptions && { valueAccessor: "value", renderOption: (o: any) => o.label })}
                />
              </div>
            );
          }
          default:
            return null;
        }
      }}
    </InputWrapper>
  );
};
